import { h, render } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'
import clsx from 'clsx'
import { BlogCard } from './BlogCard'

// Get blog data from window object
const data = window.globals?.currentBlog?.articles || []

// Custom hook to get the previous value of a state or prop
function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

// Custom hook for URL synchronization
function usePageSync() {
  const [currentPage, setCurrentPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')
    return pageParam ? parseInt(pageParam, 10) : 1
  })

  // Update URL when page changes
  useEffect(() => {
    const url = new URL(window.location.href)
    if (currentPage > 1) {
      url.searchParams.set('page', currentPage.toString())
    } else {
      url.searchParams.delete('page')
    }
    if (url.href !== window.location.href) {
      window.history.pushState({ page: currentPage }, '', url.toString())
    }
  }, [currentPage])

  // Handle back/forward browser buttons
  useEffect(() => {
    const onPopState = (event) => {
      const urlParams = new URLSearchParams(window.location.search)
      const pageParam = urlParams.get('page')
      const newPage = event.state?.page ?? (pageParam ? parseInt(pageParam, 10) : 1)
      setCurrentPage(newPage)
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  return [currentPage, setCurrentPage]
}

// Custom hook for page tracking
function usePageTracker() {
  const [currentPage, setCurrentPage] = usePageSync()
  const [loadedPages, setLoadedPages] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1
    return [initialPage]
  })

  // Track loaded pages
  useEffect(() => {
    if (!loadedPages.includes(currentPage)) {
      setLoadedPages((prev) => {
        const next = new Set(prev)
        next.add(currentPage)
        const sortedArray = [...next].sort((a, b) => a - b)
        return sortedArray
      })
    }
  }, [currentPage])

  return { loadedPages, currentPage, setCurrentPage }
}

// Custom hook for infinite scroll results
function useInfiniteScrollResults() {
  const { loadedPages, setCurrentPage } = usePageTracker()
  const prevLoadedPages = usePrevious(loadedPages)
  const [allBlogs, setAllBlogs] = useState([])
  const [gridChange, setGridChange] = useState()
  const [isLoading] = useState(false)

  // Get pagination settings
  const getDomBlogs = document.getElementById('list-blogs')
  const resultsPerPage = parseInt(getDomBlogs?.getAttribute('per-page') || 9)

  // Update all blogs when new pages are loaded
  useEffect(() => {
    if (!loadedPages || loadedPages.length === 0) return

    const justLoadedPages = loadedPages.filter(p => !(prevLoadedPages || []).includes(p))

    if (justLoadedPages.length === 0) return

    // We assume only one page is loaded at a time for this logic
    const newPage = justLoadedPages[0]

    const firstPageIndex = (newPage - 1) * resultsPerPage
    const newPageBlogs = data?.slice(firstPageIndex, firstPageIndex + resultsPerPage) || []

    if (!prevLoadedPages || prevLoadedPages.length === 0) {
      // This is the initial load. `prevLoadedPages` is undefined.
      // `justLoadedPages` will contain the page from the URL.
      setGridChange({ type: 'initial' })
      setAllBlogs(newPageBlogs)
    } else if (newPage < prevLoadedPages[0]) {
      // A previous page was loaded, so prepend it
      setGridChange({ type: 'prev' })
      setAllBlogs(prev => [...newPageBlogs, ...prev])
    } else if (newPage > prevLoadedPages[prevLoadedPages.length - 1]) {
      // A next page was loaded, so append it
      setGridChange({ type: 'next' })
      setAllBlogs(prev => [...prev, ...newPageBlogs])
    }
  }, [loadedPages, resultsPerPage, prevLoadedPages])

  const totalPages = Math.ceil(data.length / resultsPerPage)

  // Navigation functions
  const goToNextPage = () => {
    const nextPage = loadedPages[loadedPages.length - 1] + 1
    if (nextPage <= totalPages) {
      setCurrentPage(nextPage)
    }
  }

  const goToPreviousPage = () => {
    if (loadedPages.length && loadedPages[0] > 1) {
      setCurrentPage(loadedPages[0] - 1)
    }
  }

  const hasNextPage = loadedPages[loadedPages.length - 1] < totalPages
  const hasPreviousPage = loadedPages.length && loadedPages[0] > 1

  return {
    allBlogs,
    gridChange,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage
  }
}

function InfiniteScrollNext({ onNextPage, hasNextPage }) {
  const { setNode, entry } = useIntersectionObserver({
    rootMargin: '300px',
    threshold: 0.1
  })

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage) {
      onNextPage()
    }
  }, [entry, hasNextPage])

  if (!hasNextPage) return null

  return (
    <div className="col-span-full flex items-center justify-center pt-margin pb-5.5 px-margin">
      <button ref={setNode} className="btn btn-primary btn-fill btn-m" onClick={onNextPage} aria-label="Load More">
        Load More
      </button>
    </div>
  )
}

function InfiniteScrollPrevious({ onPreviousPage, hasPreviousPage }) {
  const hasBeenClicked = useRef(false)
  const { setNode, entry } = useIntersectionObserver({
    rootMargin: '100px',
    threshold: 1
  })

  useEffect(() => {
    if (!hasBeenClicked.current) return
    if (entry?.isIntersecting) {
      onPreviousPage()
    }
  }, [entry])

  const handleClick = () => {
    hasBeenClicked.current = true
    onPreviousPage()
  }

  if (!hasPreviousPage) return null

  return (
    <div className="col-span-full flex items-center justify-center p-margin">
      <button ref={setNode} className="btn btn-primary btn-fill btn-m" onClick={handleClick} aria-label="Load Previous">
        Load Previous
      </button>
    </div>
  )
}

function BlogGrid() {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const { allBlogs, gridChange, isLoading, hasNextPage, hasPreviousPage, goToNextPage, goToPreviousPage } = useInfiniteScrollResults()

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()

  useScrollCache([allBlogs, firstLoad])

  // Don't render if no data
  if (!data || data.length === 0) {
    return <div><p className="paragraph-m">No blog posts found.</p></div>
  }

  return (
    <div className="grow flex flex-col">
      <div
        ref={(el) => { setScrollElement(el); setEntry(el) }}
        className={clsx('grid grid-cols-1 md:grid-cols-3 xxl:grid-cols-4 [grid-auto-flow:row_dense] gap-px w-full overflow-hidden', (isLoading && firstLoad) && 'min-h-screen-height')}
      >
        <InfiniteScrollPrevious
          onPreviousPage={goToPreviousPage}
          hasPreviousPage={hasPreviousPage}
        />
        {
          allBlogs?.map((blog, index) => {
            let tileClass = 'tile-s'
            if ((index - 8) % 12 === 0 && index >= 8) {
              tileClass = 'tile-l'
            } else if ((index - 3) % 12 === 0 && index >= 3) {
              tileClass = 'tile-m'
            }
            return <BlogCard blog={blog} key={blog.id} dataIndex={index.toString()} tileClass={tileClass} />
          })
        }
        {allBlogs.length > 0 && !firstLoad && (
          <InfiniteScrollNext
            onNextPage={goToNextPage}
            hasNextPage={hasNextPage}
          />
        )}
      </div>
    </div>
  )
}

function RenderContent() {
  scrollHistoryManager.updateHistoryState()

  return <BlogGrid />
}

// Wait for DOM to be ready and data to be available
function initBlogGrid() {
  const container = document.getElementById('list-blogs')
  if (container && window.globals?.currentBlog?.articles) {
    render(<RenderContent />, container)
  } else {
    // Retry after a short delay if data isn't ready yet
    setTimeout(initBlogGrid, 100)
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlogGrid)
} else {
  initBlogGrid()
}
